import { useState, useEffect } from 'react';
import { UserPlus, Check, X, RefreshCw, Clock, Mail, User, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import OnboardingWizard from './OnboardingWizard';

const DENIAL_REASONS = [
  { code: 'not_authorized', label: 'Not Authorized', description: 'You are not authorized to access this system.' },
  { code: 'invalid_info', label: 'Invalid Information', description: 'The information provided could not be verified.' },
  { code: 'duplicate', label: 'Duplicate Account', description: 'An account with similar details already exists.' },
  { code: 'policy', label: 'Policy Violation', description: 'Your request does not meet our access policy requirements.' },
  { code: 'other', label: 'Other (Custom)', description: 'Provide a custom reason.' }
];

const PendingRegistrations = ({ hideHeader = false }) => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [rejectReason, setRejectReason] = useState({});
  const [customReason, setCustomReason] = useState({});
  const [showDenial, setShowDenial] = useState({});
  const [wizardUser, setWizardUser] = useState(null);

  useEffect(() => { loadPendingUsers(); }, []);

  const loadPendingUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/platform/pending-registrations', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPendingUsers(data.users || []);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to load pending registrations');
      }
    } catch (error) {
      console.error('Failed to load pending registrations:', error);
      toast.error('Failed to load pending registrations');
    } finally {
      setLoading(false);
    }
  };

  const rejectUser = async (userId, user) => {
    const reasonCode = rejectReason[userId] || '';
    const custom = customReason[userId] || '';
    if (!reasonCode) { toast.error('Please select a reason for denial'); return; }
    if (reasonCode === 'other' && !custom.trim()) { toast.error('Please provide a custom reason'); return; }
    const selectedReason = DENIAL_REASONS.find(function(r) { return r.code === reasonCode; });
    const displayReason = reasonCode === 'other' ? custom : selectedReason?.description;
    if (!confirm('Are you sure you want to deny this registration?\n\nUser: ' + user.name + ' (' + user.email + ')\nReason: ' + displayReason)) return;
    setProcessing({ ...processing, [userId]: 'deny' });
    try {
      const response = await fetch('/api/platform/pending-registrations/' + userId + '/deny', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: displayReason })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Registration denied.');
        loadPendingUsers();
        setRejectReason({ ...rejectReason, [userId]: '' });
        setCustomReason({ ...customReason, [userId]: '' });
        setShowDenial({ ...showDenial, [userId]: false });
      } else {
        toast.error(data.error || 'Failed to deny registration');
      }
    } catch (error) {
      toast.error('Failed to deny registration');
    } finally {
      setProcessing({ ...processing, [userId]: null });
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  if (loading) {
    return (
      <div className="pending-loading">
        <RefreshCw size={24} className="spin" />
        <p>Loading pending registrations...</p>
      </div>
    );
  }

  return (
    <div className="pending-registrations">
      {!hideHeader && (
        <div className="pending-header">
          <h3 className="pending-title">
            <UserPlus size={20} />
            Pending Registrations
            {pendingUsers.length > 0 && <span className="pending-count">{pendingUsers.length}</span>}
          </h3>
          <button className="btn btn-secondary btn-sm" onClick={loadPendingUsers} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      )}

      {pendingUsers.length === 0 ? (
        <div className="pending-empty">
          <UserPlus size={48} />
          <p>No pending registrations</p>
          <p className="pending-empty-hint">New registration requests will appear here for approval</p>
        </div>
      ) : (
        <div className="pending-list">
          {pendingUsers.map(function(user) {
            return (
              <div key={user.id} className="pending-user-card">
                <div className="pending-user-header">
                  <div>
                    <div className="pending-user-info">
                      <User size={16} />
                      <span className="pending-user-name">{user.name}</span>
                      <span className="status-badge-pending">PENDING</span>
                    </div>
                    <div className="pending-user-email">
                      <Mail size={14} />
                      {user.email}
                    </div>
                  </div>
                  <div className="pending-user-date">
                    <Clock size={14} />
                    {formatDate(user.createdAt)}
                  </div>
                </div>

                {showDenial[user.id] && (
                  <div className="denial-section">
                    <div className="denial-header">
                      <AlertTriangle size={14} />
                      <span>Denial Reason</span>
                    </div>
                    <div className="denial-options">
                      <div className="denial-select">
                        <select className="form-select" value={rejectReason[user.id] || ''} onChange={(e) => setRejectReason({ ...rejectReason, [user.id]: e.target.value })}>
                          <option value="">-- Select denial reason --</option>
                          {DENIAL_REASONS.map(function(reason) { return <option key={reason.code} value={reason.code}>{reason.label}</option>; })}
                        </select>
                      </div>
                      {rejectReason[user.id] === 'other' && (
                        <div className="denial-custom">
                          <input type="text" className="form-input" placeholder="Enter custom reason..." value={customReason[user.id] || ''} onChange={(e) => setCustomReason({ ...customReason, [user.id]: e.target.value })} />
                        </div>
                      )}
                    </div>
                    {rejectReason[user.id] && rejectReason[user.id] !== 'other' && (
                      <p className="denial-preview">User will receive: "{DENIAL_REASONS.find(function(r) { return r.code === rejectReason[user.id]; })?.description}"</p>
                    )}
                  </div>
                )}

                <div className="pending-actions-row">
                  {showDenial[user.id] ? (
                    <>
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowDenial({ ...showDenial, [user.id]: false })}>Cancel</button>
                      <button className="btn btn-danger btn-sm" onClick={() => rejectUser(user.id, user)} disabled={processing[user.id]} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {processing[user.id] === 'deny' ? <RefreshCw size={14} className="spin" /> : <X size={14} />} Confirm Deny
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-primary btn-sm" onClick={() => setWizardUser(user)} disabled={processing[user.id]} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Check size={14} /> Approve & Setup
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => setShowDenial({ ...showDenial, [user.id]: true })} disabled={processing[user.id]} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <X size={14} /> Deny
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {wizardUser && (
        <OnboardingWizard
          user={wizardUser}
          onClose={() => setWizardUser(null)}
          onComplete={() => { setWizardUser(null); loadPendingUsers(); }}
        />
      )}
    </div>
  );
};

export default PendingRegistrations;
